import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { adminClubsAPI, adminStatsAPI } from '../services/adminApi';
import { useAdminSeasonPhaseFilter } from '../hooks/useAdminSeasonPhaseFilter';
import AdminSeasonPhaseFilters from './AdminSeasonPhaseFilters';
import { filterPhasesForSeason } from '../utils/adminPhaseDefaults';
import { downloadPartnerClubPdf } from '../utils/partnerClubReportPdf';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: flex-end;
  padding: 1rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
  select,
  input {
    min-width: 160px;
    padding: 0.45rem 0.65rem;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 400;
    text-transform: none;
    color: #0f172a;
    background: #fff;
  }
`;

const Btn = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  border: ${(p) => (p.$secondary ? '1px solid #043663' : 'none')};
  background: ${(p) => (p.$secondary ? '#fff' : '#043663')};
  color: ${(p) => (p.$secondary ? '#043663' : '#fff')};
  font-weight: 700;
  font-size: 0.85rem;
  cursor: pointer;
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 0.75rem;
`;

const Kpi = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.9rem 1rem;
  h4 {
    margin: 0 0 0.35rem;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #94a3b8;
  }
  .val {
    font-size: 1.35rem;
    font-weight: 800;
    color: #0f172a;
  }
  .sub {
    margin-top: 0.25rem;
    font-size: 0.75rem;
    color: #64748b;
  }
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1rem 1.15rem;
  h3 {
    margin: 0 0 0.75rem;
    font-size: 1rem;
    font-weight: 700;
    color: #0f172a;
  }
`;

const NaList = styled.ul`
  margin: 0;
  padding-left: 1.1rem;
  color: #92400e;
  font-size: 0.85rem;
  li {
    margin-bottom: 0.35rem;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  th,
  td {
    text-align: left;
    padding: 0.4rem 0.35rem;
    border-bottom: 1px solid #f1f5f9;
  }
  th {
    color: #64748b;
    font-size: 0.7rem;
    text-transform: uppercase;
  }
`;

const Hint = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: #64748b;
  line-height: 1.45;
`;

function defaultRange() {
  const to = new Date();
  const from = new Date();
  from.setMonth(from.getMonth() - 3);
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { from: fmt(from), to: fmt(to) };
}

/** Phase/API dates → YYYY-MM-DD for <input type="date"> */
function toInputDate(v) {
  if (v == null || v === '') return null;
  const s = String(v);
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

/** Oldest start → latest end across a list of phases */
function dateRangeFromPhases(phases) {
  let min = null;
  let max = null;
  for (const p of phases || []) {
    const f = toInputDate(p.start_date);
    const t = toInputDate(p.end_date);
    if (f && (!min || f < min)) min = f;
    if (t && (!max || t > max)) max = t;
  }
  return { from: min, to: max };
}

function fmtNum(n, digits = 1) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString(undefined, {
    maximumFractionDigits: digits,
    minimumFractionDigits: 0,
  });
}

const DAY_LABELS = {
  sun: 'Sunday',
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
};

function csvCell(v) {
  const s = v == null || v === '' ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function csvBlock(rows) {
  return rows.map((row) => row.map(csvCell).join(',')).join('\n');
}

function clubFileSlug(name) {
  return (
    String(name || 'club')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'club'
  );
}

function unwrapReport(res) {
  return res?.data || res || null;
}

function buildPartnerClubCsv(report) {
  const club = report.club || {};
  const period = report.period || {};
  const u = report.utilisation || {};
  const peak = u.peakSplit || {};
  const occ = u.occupancy || {};
  const players = report.players || {};
  const retention = report.retention || {};
  const peer = report.peerBenchmark || {};
  const divisions = report.divisions || {};

  const sections = [
    csvBlock([
      ['Partner club report'],
      ['Club', club.name || ''],
      ['Canton', club.canton || ''],
      ['Partner', club.isPartner ? 'Yes' : 'No'],
      ['From', period.from || ''],
      ['To', period.to || ''],
      ['Hourly rate CHF', club.hourlyRateChf ?? ''],
      ['Slot duration hours', u.slotDurationHours ?? club.slotDurationHours ?? ''],
      [],
      ['Metric', 'Value'],
      ['Matches', u.matchCount ?? 0],
      ['Court hours', u.courtHours ?? ''],
      ['Estimated revenue CHF', u.estimatedRevenueChf ?? 'N/A'],
      ['Peak matches', peak.peak ?? 'N/A'],
      ['Off-peak matches', peak.offPeak ?? 'N/A'],
      ['Peak share %', peak.peakPct ?? 'N/A'],
      ['Occupancy %', occ.occupancyPct ?? 'N/A'],
      ['HPS hours', occ.hpsHours ?? ''],
      ['Available court hours', occ.availableCourtHours ?? ''],
      ['Unique players', players.uniquePlayers ?? 0],
      ['Retention %', retention.retentionPct ?? ''],
      ['Retained players', retention.retainedPlayers ?? ''],
      ['Prior-period unique players', retention.previousUniquePlayers ?? ''],
      ['Prior period from', retention.previousFrom || ''],
      ['Prior period to', retention.previousTo || ''],
      ['Peer benchmark', peer.label || 'N/A'],
      ['Partner clubs in benchmark', peer.partnerClubCount ?? ''],
    ]),
  ];

  if (report.notAvailable?.length) {
    sections.push(
      csvBlock([
        ['Metrics needing club setup'],
        ['Metric', 'Reason'],
        ...report.notAvailable.map((n) => [n.metric, n.reason]),
      ])
    );
  }

  sections.push(
    csvBlock([
      ['Age distribution'],
      ['Known DOB', players.age?.known ?? 0],
      ['Unknown DOB', players.age?.unknown ?? 0],
      ['Band', 'Players'],
      ...((players.age?.bands || []).length
        ? players.age.bands.map((b) => [b.band, b.count])
        : [['No DOB data', '']]),
    ])
  );

  sections.push(
    csvBlock([
      ['Gender split'],
      ['Known', players.sex?.known ?? 0],
      ['Unknown', players.sex?.unknown ?? 0],
      ['Sex', 'Players'],
      ...((players.sex?.bands || []).length
        ? players.sex.bands.map((b) => [b.sex, b.count])
        : [['No sex data', '']]),
    ])
  );

  sections.push(
    csvBlock([
      ['Division breakdown (teams)'],
      ['Teams with division', divisions.teamsWithDivision ?? 0],
      ['Teams in matches', divisions.teamsInMatches ?? 0],
      ['Division / level', 'Teams'],
      ...((divisions.bands || []).length
        ? divisions.bands.map((b) => [b.label, b.count])
        : [['No division assignments', '']]),
    ])
  );

  sections.push(
    csvBlock([
      ['Date x time heatmap'],
      ['Date', 'Day', 'Hour', 'Matches'],
      ...((u.heatmap || []).length
        ? u.heatmap.map((h) => [
            h.date || '',
            DAY_LABELS[h.day] ||
              (h.day === 'unk' || h.day === 'undefined' ? 'Unknown' : h.day || ''),
            h.hour === 'unknown' || h.hour == null ? '' : `${h.hour}:00`,
            h.count,
          ])
        : [['No matches with venue details in this range', '', '', '']]),
    ])
  );

  return `\uFEFF${sections.join('\n\n')}\n`;
}

function downloadPartnerClubCsv(report) {
  const name = clubFileSlug(report?.club?.name);
  const from = report?.period?.from || '';
  const to = report?.period?.to || '';
  const blob = new Blob([buildPartnerClubCsv(report)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `partner-club-${name}-${from}-to-${to}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function AdminPartnerClubReport() {
  const defaults = useMemo(() => defaultRange(), []);
  const [clubId, setClubId] = useState('');
  const [from, setFrom] = useState(defaults.from);
  const [to, setTo] = useState(defaults.to);
  const [submitted, setSubmitted] = useState(null);
  const [exporting, setExporting] = useState(null);

  const {
    campaignId,
    seasonId,
    onCampaignChange,
    onPhaseChange,
    seasonOptions,
    phaseOptions,
    allPhases,
  } = useAdminSeasonPhaseFilter({ allowAll: true, autoDefault: false });

  // Sync From/To from filter scope:
  // - specific phase → that phase's dates
  // - season + all phases → oldest/latest phase in that season
  // - all seasons + all phases → oldest/latest across all phases
  const prevFilterKey = useRef('');
  useEffect(() => {
    const key = `${campaignId || ''}|${seasonId || ''}|${(allPhases || []).length}`;
    if (!allPhases?.length) return;
    if (key === prevFilterKey.current) return;
    prevFilterKey.current = key;

    let scope;
    if (seasonId) {
      scope = allPhases.filter((p) => String(p.id) === String(seasonId));
    } else if (campaignId) {
      scope = filterPhasesForSeason(allPhases, campaignId);
    } else {
      scope = allPhases;
    }
    const range = dateRangeFromPhases(scope);
    if (range.from) setFrom(range.from);
    if (range.to) setTo(range.to);
  }, [campaignId, seasonId, allPhases]);

  const { data: clubsRes, isLoading: clubsLoading, isError: clubsError, error: clubsErr } = useQuery(
    ['admin-clubs-partner-report'],
    () => adminClubsAPI.list({ includeInactive: false }),
    { staleTime: 60 * 1000, retry: false }
  );

  const clubs = useMemo(() => {
    // adminClubsAPI.list already unwraps to the clubs array; tolerate { data: [] } too
    const rows = Array.isArray(clubsRes)
      ? clubsRes
      : Array.isArray(clubsRes?.data)
        ? clubsRes.data
        : [];
    return [...rows].sort((a, b) => {
      if (!!b.isPartner !== !!a.isPartner) return a.isPartner ? -1 : 1;
      return String(a.name || '').localeCompare(String(b.name || ''));
    });
  }, [clubsRes]);

  const reportEnabled = !!(submitted?.clubId && submitted?.from && submitted?.to);

  const { data: reportRes, isLoading, isError, error, isFetching } = useQuery(
    [
      'partner-club-report',
      submitted?.clubId || '',
      submitted?.from || '',
      submitted?.to || '',
      submitted?.seasonId || '',
    ],
    () =>
      adminStatsAPI.getPartnerClubReport({
        clubId: submitted.clubId,
        from: submitted.from,
        to: submitted.to,
        seasonId: submitted.seasonId || undefined,
      }),
    {
      enabled: reportEnabled,
      retry: false,
      keepPreviousData: true,
    }
  );

  const report = reportRes?.data || reportRes;

  const run = () => {
    if (!clubId || !from || !to) return;
    setSubmitted({
      clubId,
      from,
      to,
      seasonId: seasonId || '',
    });
  };

  const filtersMatchLoaded =
    reportEnabled &&
    submitted?.clubId === clubId &&
    submitted?.from === from &&
    submitted?.to === to &&
    (submitted?.seasonId || '') === (seasonId || '');

  const loadExportPayload = async () => {
    if (!clubId || !from || !to) {
      toast.error('Pick a club and date range first.');
      return null;
    }
    let payload = filtersMatchLoaded && report && !isError ? report : null;
    if (!payload) {
      const next = {
        clubId,
        from,
        to,
        seasonId: seasonId || '',
      };
      setSubmitted(next);
      const res = await adminStatsAPI.getPartnerClubReport({
        clubId: next.clubId,
        from: next.from,
        to: next.to,
        seasonId: next.seasonId || undefined,
      });
      payload = unwrapReport(res);
    }
    if (!payload?.club) {
      throw new Error('No report data to export.');
    }
    return payload;
  };

  const exportReport = async (kind) => {
    setExporting(kind);
    try {
      const payload = await loadExportPayload();
      if (!payload) return;
      if (kind === 'pdf') downloadPartnerClubPdf(payload);
      else downloadPartnerClubCsv(payload);
      toast.success('Download started.');
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || 'Export failed');
    } finally {
      setExporting(null);
    }
  };

  return (
    <Wrap>
      <Hint>
        Matches are attributed by venue name from match details (club name / aliases), not home club.
        From/To follow the filter: a specific phase uses that phase’s dates; a season with “All phases”
        uses the oldest phase start → latest phase end; “All seasons” uses the full chronological span.
        You can still edit From/To afterward. Export CSV or PDF downloads this club report (summary, demographics,
        divisions, heatmap). Configure rate, opening hours, and peak windows under{' '}
        <strong>Settings → Clubs</strong>.
      </Hint>

      <Filters>
        <Field>
          Club
          <select
            value={clubId}
            disabled={clubsLoading}
            onChange={(e) => setClubId(e.target.value)}
          >
            <option value="">
              {clubsLoading
                ? 'Loading clubs…'
                : clubsError
                  ? 'Failed to load clubs'
                  : clubs.length
                    ? 'Select club…'
                    : 'No clubs found'}
            </option>
            {clubs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.isPartner ? '★ ' : ''}
                {c.name}
                {c.canton ? ` (${c.canton})` : ''}
              </option>
            ))}
          </select>
        </Field>
        <Field>
          From
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field>
          To
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </Field>
        <AdminSeasonPhaseFilters
          campaignId={campaignId}
          seasonId={seasonId}
          seasonOptions={seasonOptions}
          phaseOptions={phaseOptions}
          onCampaignChange={onCampaignChange}
          onPhaseChange={onPhaseChange}
          allowAll
          allPhasesLabel="All phases (date range only)"
        />
        <Btn type="button" disabled={!clubId || !from || !to || isFetching} onClick={run}>
          {isFetching ? 'Loading…' : 'Run report'}
        </Btn>
        <Btn
          type="button"
          $secondary
          disabled={!clubId || !from || !to || !!exporting || isFetching}
          onClick={() => exportReport('csv')}
        >
          {exporting === 'csv' ? 'Exporting…' : 'Export CSV'}
        </Btn>
        <Btn
          type="button"
          $secondary
          disabled={!clubId || !from || !to || !!exporting || isFetching}
          onClick={() => exportReport('pdf')}
        >
          {exporting === 'pdf' ? 'Exporting…' : 'Export PDF'}
        </Btn>
      </Filters>

      {!submitted ? (
        <Card>
          <Hint>Pick a club and date range, then run the report.</Hint>
        </Card>
      ) : null}

      {clubsError ? (
        <Card>
          <Hint style={{ color: '#b91c1c' }}>
            Could not load clubs:{' '}
            {clubsErr?.response?.data?.message || clubsErr?.message || 'Unknown error'}.
            You need Settings → Clubs read access, or ask an admin to grant it.
          </Hint>
        </Card>
      ) : null}

      {!clubsLoading && !clubsError && clubs.length === 0 ? (
        <Card>
          <Hint>
            No clubs in the catalog for this tenant. Add them under <strong>Settings → Clubs</strong>.
          </Hint>
        </Card>
      ) : null}

      {isError ? (
        <Card>
          <Hint style={{ color: '#b91c1c' }}>
            {error?.response?.data?.message || error?.message || 'Failed to load report'}
          </Hint>
        </Card>
      ) : null}

      {isLoading && submitted ? (
        <Card>
          <Hint>Loading partner club report…</Hint>
        </Card>
      ) : null}

      {report && !isError ? (
        <>
          <KpiGrid>
            <Kpi>
              <h4>Matches</h4>
              <div className="val">{fmtNum(report.utilisation?.matchCount, 0)}</div>
              <div className="sub">At this venue in range</div>
            </Kpi>
            <Kpi>
              <h4>Court hours</h4>
              <div className="val">{fmtNum(report.utilisation?.courtHours)}</div>
              <div className="sub">
                × {fmtNum(report.utilisation?.slotDurationHours)} h / match
              </div>
            </Kpi>
            <Kpi>
              <h4>Est. revenue</h4>
              <div className="val">
                {report.utilisation?.estimatedRevenueChf != null
                  ? `CHF ${fmtNum(report.utilisation.estimatedRevenueChf, 0)}`
                  : 'N/A'}
              </div>
              <div className="sub">
                {report.club?.hourlyRateChf != null
                  ? `@ CHF ${fmtNum(report.club.hourlyRateChf, 0)}/h`
                  : 'Set hourly rate on club'}
              </div>
            </Kpi>
            <Kpi>
              <h4>Peak share</h4>
              <div className="val">
                {report.utilisation?.peakSplit
                  ? `${fmtNum(report.utilisation.peakSplit.peakPct)}%`
                  : 'N/A'}
              </div>
              <div className="sub">
                {report.utilisation?.peakSplit
                  ? `${report.utilisation.peakSplit.peak} peak / ${report.utilisation.peakSplit.offPeak} off-peak`
                  : 'Set peak windows on club'}
              </div>
            </Kpi>
            <Kpi>
              <h4>Occupancy</h4>
              <div className="val">
                {report.utilisation?.occupancy
                  ? `${fmtNum(report.utilisation.occupancy.occupancyPct)}%`
                  : 'N/A'}
              </div>
              <div className="sub">
                {report.utilisation?.occupancy
                  ? `${fmtNum(report.utilisation.occupancy.hpsHours)} / ${fmtNum(report.utilisation.occupancy.availableCourtHours)} h`
                  : 'Set courts + opening hours'}
              </div>
            </Kpi>
            <Kpi>
              <h4>Unique players</h4>
              <div className="val">{fmtNum(report.players?.uniquePlayers, 0)}</div>
              <div className="sub">On teams that played here</div>
            </Kpi>
            <Kpi>
              <h4>Retention</h4>
              <div className="val">
                {report.retention?.retentionPct != null
                  ? `${fmtNum(report.retention.retentionPct)}%`
                  : '—'}
              </div>
              <div className="sub">
                {report.retention
                  ? `${report.retention.retainedPlayers} of ${report.retention.previousUniquePlayers} prior period`
                  : ''}
              </div>
            </Kpi>
            <Kpi>
              <h4>Peer benchmark</h4>
              <div className="val" style={{ fontSize: '0.95rem', lineHeight: 1.3 }}>
                {report.peerBenchmark?.label || 'N/A'}
              </div>
              <div className="sub">
                {report.peerBenchmark
                  ? `${report.peerBenchmark.partnerClubCount} partner clubs`
                  : 'Mark clubs as Partner'}
              </div>
            </Kpi>
          </KpiGrid>

          {report.notAvailable?.length ? (
            <Card>
              <h3>Metrics needing club setup</h3>
              <NaList>
                {report.notAvailable.map((n) => (
                  <li key={n.metric}>
                    <strong>{n.metric}</strong>: {n.reason}
                  </li>
                ))}
              </NaList>
            </Card>
          ) : null}

          <Card>
            <h3>Demographics</h3>
            <Hint style={{ marginBottom: '0.75rem' }}>
              From roster players on teams that played matches at this venue in the selected range.
              Unique players: {fmtNum(report.players?.uniquePlayers, 0)}.
            </Hint>

            <h3 style={{ fontSize: '0.95rem', marginTop: '0.5rem' }}>Age distribution</h3>
            <Hint style={{ marginBottom: '0.5rem' }}>
              Known DOB {report.players?.age?.known ?? 0} · unknown {report.players?.age?.unknown ?? 0}
              {report.players?.age?.unknown > 0
                ? ' (no date of birth on player / team member profile)'
                : ''}
            </Hint>
            <Table>
              <thead>
                <tr>
                  <th>Band</th>
                  <th>Players</th>
                </tr>
              </thead>
              <tbody>
                {(report.players?.age?.bands || []).map((b) => (
                  <tr key={b.band}>
                    <td>{b.band}</td>
                    <td>{b.count}</td>
                  </tr>
                ))}
                {!report.players?.age?.bands?.length ? (
                  <tr>
                    <td colSpan={2}>No DOB data for players in this set</td>
                  </tr>
                ) : null}
              </tbody>
            </Table>

            <h3 style={{ fontSize: '0.95rem', marginTop: '1.25rem' }}>Gender split</h3>
            <Hint style={{ marginBottom: '0.5rem' }}>
              Known {report.players?.sex?.known ?? 0} · unknown {report.players?.sex?.unknown ?? 0}
              {report.players?.sex?.unknown > 0
                ? ' — those players have no sex/gender set on their player or team member profile'
                : ''}
            </Hint>
            <Table>
              <thead>
                <tr>
                  <th>Sex</th>
                  <th>Players</th>
                </tr>
              </thead>
              <tbody>
                {(report.players?.sex?.bands || []).map((b) => (
                  <tr key={b.sex}>
                    <td>{b.sex}</td>
                    <td>{b.count}</td>
                  </tr>
                ))}
                {!report.players?.sex?.bands?.length ? (
                  <tr>
                    <td colSpan={2}>No sex data for players in this set</td>
                  </tr>
                ) : null}
              </tbody>
            </Table>
          </Card>

          <Card>
            <h3>Division breakdown (teams)</h3>
            <Hint style={{ marginBottom: '0.5rem' }}>
              {report.divisions?.teamsWithDivision ?? 0} of {report.divisions?.teamsInMatches ?? 0}{' '}
              teams have a division assignment
            </Hint>
            <Table>
              <thead>
                <tr>
                  <th>Division / level</th>
                  <th>Teams</th>
                </tr>
              </thead>
              <tbody>
                {(report.divisions?.bands || []).map((b) => (
                  <tr key={b.label}>
                    <td>{b.label}</td>
                    <td>{b.count}</td>
                  </tr>
                ))}
                {!report.divisions?.bands?.length ? (
                  <tr>
                    <td colSpan={2}>No division assignments for these teams</td>
                  </tr>
                ) : null}
              </tbody>
            </Table>
          </Card>

          <Card>
            <h3>Date × time heatmap</h3>
            <Table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Day</th>
                  <th>Hour</th>
                  <th>Matches</th>
                </tr>
              </thead>
              <tbody>
                {(report.utilisation?.heatmap || []).map((h) => (
                  <tr key={`${h.date || 'unk'}-${h.hour}`}>
                    <td>{h.date || '—'}</td>
                    <td>
                      {DAY_LABELS[h.day] ||
                        (h.day === 'unk' || h.day === 'undefined' ? 'Unknown' : h.day)}
                    </td>
                    <td>{h.hour === 'unknown' ? '—' : `${h.hour}:00`}</td>
                    <td>{h.count}</td>
                  </tr>
                ))}
                {!report.utilisation?.heatmap?.length ? (
                  <tr>
                    <td colSpan={4}>No matches with venue details in this range</td>
                  </tr>
                ) : null}
              </tbody>
            </Table>
          </Card>
        </>
      ) : null}
    </Wrap>
  );
}
